import { Box, SxProps, Theme } from '@mui/material';
import { useSweepFields } from '../../../sweep-fields/useCachedSweepFields';
import {
  NestedSelector,
  NestedItem,
  NestedSelectorPath,
  NestedSelectorPathFragment,
  NestedSelectorRef,
} from '../NestedSelector/NestedSelector';
import { useLoadingItem } from './useLoadingItem';
import AddFieldButton from './AddFieldButton';
import FieldSelectorEmptyState from './FieldSelectorEmptyState';
import { SFDCObjectType } from '../../../types/enums/SFDCObjectType';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  injectPolymorphicFields,
  parseSweepFieldPolymorphicID,
} from './nestedFieldsSelectorHelper';
import { EditMode } from '../../../constants/fieldsManagementConsts';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';
import CreateEditFieldDialog from '../../pages/funnel-map-flow/dialogs/deprecated-fields-management/create-field-dialog/CreateEditFieldDialog';
import { useCreateFieldContext } from '../../pages/funnel-map-flow/dialogs/deprecated-fields-management/create-field-dialog/CreateFieldContext';
import { Button, colors } from '@sweep-io/sweep-design';
import { GenericMenu, useGenericMenu } from '../GenericMenu';
import { FieldContext } from '../../../types/enums/FieldContext';
import { telemetry } from '../../../telemetry';
import { ChevronRight } from '@sweep-io/sweep-design/dist/icons';
import { useTimeout } from '../../../hooks/useTimeout';
import { SfdcDisplayType, SweepField } from '../../../types/fields';
import { SF_NUMBERFIELD_TYPES, SF_TEXT_FIELD_TYPES } from '../../../types/ReferencedValuesTypes';
import uniq from 'lodash/uniq';

export interface NestedFieldsField {
  sweepField: SweepField;
  fieldIds: string[];
  sfDisplayType: SfdcDisplayType;
  fieldLabels: string[];
  fullPath: NestedSelectorPath;
  fieldContext?: FieldContext;
  valueContext?: FieldContext;
}

export interface NestedFieldsSelectorProps {
  nestedPath: NestedSelectorPath; // Controlled
  objectType: string;
  onChange?: (field: NestedFieldsField) => any;
  additionalPopoverStyles?: SxProps<Theme>;
  crmOrgId: string;
  readonly?: boolean;
  startIcon?: React.ReactNode;
  customButtonText?: string;
  customButtonSx?: SxProps;
  useCustomButton?: boolean;
  displayLeadingObjectName?: boolean;

  customButtonStartIcon?: React.ReactNode;
  customButtonEndIcon?: React.ReactNode;
  CustomButtonComponent?: React.FunctionComponent<{ onClick: (i: any) => void; disabled: boolean }>;
  placeholder?: string;
  filterBy?: (field: SweepField) => boolean;
  hideActionButton?: boolean;
  nestedSelectorButtonStyle?: SxProps<Theme>;
  dropDownWidth?: number;
  showApiNameTooltip?: boolean;
  restrictReferencesToObject?: string;
  fieldContext?: FieldContext;
  displayFieldContextMenu?: boolean;
  valueContext?: FieldContext;
  displayValueContextMenu?: boolean;

  // If a field is polymorphic, it will be resolved to multiple fields.
  // One selectable, and one drill in field per object.
  // This flag will disable that behavior.
  disableResolvePolymorphic?: boolean;

  // If a field is a lookup, it will be resolve into at least two fields.
  // One selectable, and one drill in field.
  // This flag will disable that behavior.
  disableLookupItemsResolve?: boolean;

  // If true, the create field dialog will be hidden.
  // This will override the create field context.
  forceHideCreateFieldDialog?: boolean;

  displayTypesFilter?: SfdcDisplayType | SfdcDisplayType[];

  /**
   * Controls how displayTypesFilter is applied:
   * - 'exclusive': Only show exact matches to the specified display types
   * - 'family': Show fields that belong to the same family as the specified display types
   */
  displayTypesFilterMode?: 'exclusive' | 'family';

  // Array of field IDs to exclude from the field display
  excludeFieldIds?: string[];
  // @deprecated use allowOnlySfType instead
  deprecated_isReferencedValue?: boolean;
}

const sweepFieldsInterfaceToNodes = (
  fields: SweepField[],
  disableLookupItemsResolve?: boolean,
  showApiNameTooltip?: boolean,
): NestedItem[] => {
  const newFields: NestedItem[] = [];
  fields.forEach((field) => {
    const { isResolvable } = parseSweepFieldPolymorphicID(field.id || '');
    if (isResolvable && disableLookupItemsResolve) {
      return;
    }
    newFields.push({
      label: field.sweepFieldName || field.sfFieldName || '',
      value: field.id || '',
      shouldResolveSubItems: disableLookupItemsResolve ? false : isResolvable,
      data: field,
      tooltip: showApiNameTooltip ? (
        <>
          <div>{field.sfFieldName}</div>
        </>
      ) : undefined,
    });
  });
  return newFields;
};

const replaceEmptyLabelsByApiName = (path: NestedSelectorPath) => {
  try {
    return path.map((path) => ({
      ...path,
      label: path.label || path.value,
    }));
  } catch (e) {
    telemetry.captureError(e);
    return [];
  }
};

const removeLookupFromPathValue = (path: NestedSelectorPath) =>
  path.map((fragment) => {
    const { id } = parseSweepFieldPolymorphicID(fragment.value);
    return {
      ...fragment,
      value: id,
    };
  });

const fieldContextMenuEntries = [
  { label: 'Triggering record', value: FieldContext.CURRENT_VALUE },
  {
    label: 'Prior value of Triggering record',
    value: FieldContext.PRIOR_VALUE,
  },
];

const getFamilyDisplayType = (displayType: SfdcDisplayType) => {
  if (SF_TEXT_FIELD_TYPES.includes(displayType)) {
    return SF_TEXT_FIELD_TYPES;
  }
  if (SF_NUMBERFIELD_TYPES.includes(displayType)) {
    return SF_NUMBERFIELD_TYPES;
  }

  return [displayType];
};

const getFamilyDisplayTypes = (displayTypes: SfdcDisplayType[]) => {
  return uniq(displayTypes.map(getFamilyDisplayType).flat());
};

export const getParentFieldRelatedTypes = (
  displayTypes: SfdcDisplayType[],
  displayTypesFilterMode: 'exclusive' | 'family',
) => {
  if (displayTypesFilterMode === 'exclusive') {
    return displayTypes;
  }
  return getFamilyDisplayTypes(displayTypes);
};

export const NestedFieldsSelector = ({
  nestedPath,
  objectType,
  onChange,
  additionalPopoverStyles,
  crmOrgId,
  readonly,
  startIcon,
  customButtonText,
  customButtonSx,
  useCustomButton,
  displayLeadingObjectName,
  deprecated_isReferencedValue,
  disableLookupItemsResolve: _disableLookupItemsResolve,
  customButtonStartIcon,
  customButtonEndIcon,
  CustomButtonComponent,
  placeholder,
  filterBy,
  hideActionButton,
  nestedSelectorButtonStyle,
  dropDownWidth,
  showApiNameTooltip = true,
  restrictReferencesToObject,
  fieldContext,
  displayFieldContextMenu,
  disableResolvePolymorphic,
  valueContext,
  displayValueContextMenu,
  forceHideCreateFieldDialog,
  displayTypesFilter,
  displayTypesFilterMode = 'exclusive',
  excludeFieldIds,
}: NestedFieldsSelectorProps) => {
  const { setSweepTimeout } = useTimeout();
  const { hideCreateFieldDialog: hideCreateFieldDialogFromCtx } = useCreateFieldContext();
  const hideCreateFieldDialog = forceHideCreateFieldDialog ?? hideCreateFieldDialogFromCtx;

  const allAllowedSfDisplayTYpes = useMemo(
    () =>
      displayTypesFilter
        ? getParentFieldRelatedTypes([displayTypesFilter].flat(), displayTypesFilterMode)
        : [],
    [displayTypesFilter, displayTypesFilterMode],
  );

  const { searchSweepFields } = useSweepFields();
  const { calculateAndSetLoadingState, loadingItem } = useLoadingItem();
  const { addNotification } = useSweepNotifications();
  const [isCreateFieldDialogOpened, setIsCreateFieldDialogOpened] = useState(false);

  const [localFieldContext, setLocalFieldContext] = useState<FieldContext | undefined>(
    displayFieldContextMenu ? fieldContext : undefined,
  );
  const [localValueContext, setLocalValueContext] = useState<FieldContext | undefined>(
    displayValueContextMenu ? valueContext : undefined,
  );

  const disableLookupItemsResolve =
    Boolean(localFieldContext === FieldContext.PRIOR_VALUE) ||
    Boolean(localValueContext === FieldContext.PRIOR_VALUE) ||
    _disableLookupItemsResolve;

  const nestedSelectorRef = useRef<NestedSelectorRef>(null);

  const filterExcludedFields = useCallback(
    (fields: SweepField[]) => {
      if (!excludeFieldIds || excludeFieldIds.length === 0) {
        return fields;
      }

      const excludedFieldIds = new Set(excludeFieldIds.filter((id): id is string => Boolean(id)));
      return fields.filter((field) => field.id && !excludedFieldIds.has(field.id));
    },
    [excludeFieldIds],
  );

  const searchOnlyReferencedValues = useCallback(
    (sweepFields: SweepField[]) => {
      const parentFieldRelatedTypes: SfdcDisplayType[] = [
        ...allAllowedSfDisplayTYpes,
        SfdcDisplayType.REFERENCE,
        SfdcDisplayType.ID,
      ];

      return sweepFields.filter((item) => {
        const currentItem = parentFieldRelatedTypes.includes(item.sfDisplayType);

        if (item.sfDisplayType === SfdcDisplayType.REFERENCE && restrictReferencesToObject) {
          return Boolean(item.objectNames?.includes(restrictReferencesToObject));
        }

        return currentItem;
      });
    },
    [allAllowedSfDisplayTYpes, restrictReferencesToObject],
  );

  const applyFilters = useCallback(
    (fields: SweepField[]) => {
      let _fields = fields;

      if (deprecated_isReferencedValue) {
        _fields = searchOnlyReferencedValues(_fields);
      }

      if (allAllowedSfDisplayTYpes.length) {
        _fields = _fields.filter((field) => {
          const { isResolvable } = parseSweepFieldPolymorphicID(field.id || '');
          return isResolvable || allAllowedSfDisplayTYpes.includes(field.sfDisplayType);
        });
      }
      if (disableLookupItemsResolve) {
        _fields = _fields.filter((field) => {
          const { isResolvable } = parseSweepFieldPolymorphicID(field.id || '');
          return !isResolvable;
        });
      }

      if (filterBy) {
        _fields = _fields.filter(filterBy);
      }
      _fields = filterExcludedFields(_fields);
      return _fields;
    },
    [
      allAllowedSfDisplayTYpes,
      deprecated_isReferencedValue,
      filterBy,
      filterExcludedFields,
      searchOnlyReferencedValues,
      disableLookupItemsResolve,
    ],
  );

  const searchBySiblings = useCallback(
    async (path: NestedSelectorPath) => {
      const { sweepFields, complete, isFirstLoad } = await searchSweepFields({
        fieldIds: removeLookupFromPathValue(path).map((v) => v.value),
        crmOrgId,
      });
      injectPolymorphicFields(sweepFields, disableResolvePolymorphic);

      calculateAndSetLoadingState(complete, isFirstLoad);

      return path.map(({ value }, idx) => {
        const objectTypeFromFieldId = value.split('.')[0];
        let fields = objectTypeFromFieldId ? sweepFields[objectTypeFromFieldId].fields : [];
        fields = applyFilters(fields);

        const items = objectTypeFromFieldId
          ? sweepFieldsInterfaceToNodes(fields, disableLookupItemsResolve, showApiNameTooltip)
          : [];
        const itemBasePath = path.slice(0, idx);

        return {
          path: itemBasePath,
          items: items,
        };
      });
    },
    [
      searchSweepFields,
      crmOrgId,
      disableResolvePolymorphic,
      calculateAndSetLoadingState,
      applyFilters,
      disableLookupItemsResolve,
      showApiNameTooltip,
    ],
  );

  const searchByObjectType = useCallback(
    async (_objectType: string) => {
      const { sweepFields, complete, isFirstLoad } = await searchSweepFields({
        objectType: [_objectType],
        crmOrgId,
      });
      injectPolymorphicFields(sweepFields, disableResolvePolymorphic);

      calculateAndSetLoadingState(complete, isFirstLoad);

      let fields = sweepFields[_objectType].fields;
      fields = applyFilters(fields);

      const items = sweepFieldsInterfaceToNodes(
        fields,
        disableLookupItemsResolve,
        showApiNameTooltip,
      );

      const path: NestedSelectorPathFragment[] = [];

      return [
        {
          path,
          items,
        },
      ];
    },
    [
      searchSweepFields,
      crmOrgId,
      disableResolvePolymorphic,
      calculateAndSetLoadingState,
      applyFilters,
      disableLookupItemsResolve,
      showApiNameTooltip,
    ],
  );

  const resolveOpen = useCallback(
    async (sweepSelectorValue: NestedSelectorPath) => {
      const hasPath = Boolean(sweepSelectorValue.length > 0);
      if (!(hasPath || objectType)) {
        return [];
      }

      if (hasPath) {
        const _sweepSelectorValue = removeLookupFromPathValue(sweepSelectorValue);

        const items = await searchBySiblings(_sweepSelectorValue);
        return items;
      } else {
        const items = await searchByObjectType(objectType as string);
        return items;
      }
    },
    [objectType, searchByObjectType, searchBySiblings],
  );

  const resolveChildren = useCallback(
    async (item: NestedItem<SweepField>) => {
      if (!item.data) {
        return [];
      }

      const { objectName, objectNames, sfDisplayType } = item.data;
      const shouldResolveChildren = sfDisplayType === SfdcDisplayType.REFERENCE && objectNames;

      if (!shouldResolveChildren) {
        return [];
      }

      // TODO: Make sure this implementation is correct
      const _objectNames: string[] =
        sfDisplayType === SfdcDisplayType.REFERENCE ? objectNames : [objectName];

      const { sweepFields } = await searchSweepFields({
        objectType: _objectNames,
        crmOrgId: crmOrgId,
      });
      injectPolymorphicFields(sweepFields, disableResolvePolymorphic);

      let fields = _objectNames.length ? sweepFields[_objectNames[0]].fields : [];

      if (deprecated_isReferencedValue) {
        fields = searchOnlyReferencedValues(fields);
      }

      if (filterBy) {
        fields = fields.filter(filterBy);
      }

      fields = filterExcludedFields(fields);

      //related to SWEEP-2989
      fields = fields.filter((field) => field.sweepFieldName !== item.data?.sweepFieldName);

      return sweepFieldsInterfaceToNodes(fields, disableLookupItemsResolve);
    },
    [
      crmOrgId,
      disableLookupItemsResolve,
      filterBy,
      deprecated_isReferencedValue,
      disableResolvePolymorphic,
      searchOnlyReferencedValues,
      searchSweepFields,
      filterExcludedFields,
    ],
  );

  //This function is called after creation of a new field
  const onChangeWithNewField = useCallback(
    (field: SweepField) => {
      const item = {
        sweepField: field,
        fieldIds: [field.id ?? ''],
        fieldLabels: [field.objectName ?? '', field.sweepFieldName ?? ''],
        fullPath: [
          {
            label: field.sweepFieldName ?? '',
            value: field.id ?? '',
          },
        ],
        sfDisplayType: field.sfDisplayType,
      };
      onChange?.(item);
    },
    [onChange],
  );

  const { handleClickOpen, handleClose, isOpen, anchorEl } = useGenericMenu();

  const priorValueSelectorHeaderComponent = () => {
    const currentContext = displayFieldContextMenu ? localFieldContext : localValueContext;
    const currentSetContext = displayFieldContextMenu ? setLocalFieldContext : setLocalValueContext;
    return (
      <>
        <Box
          sx={{
            margin: 2,
            marginBottom: 0,
            '& > .MuiButtonBase-root': {
              width: '100%',
              '&:hover': {
                svg: {
                  path: {
                    stroke: colors.grey[900],
                  },
                },
              },
            },
          }}
        >
          <Button size="medium" variant="flat-grey" onClick={handleClickOpen}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {currentContext === FieldContext.PRIOR_VALUE
                ? fieldContextMenuEntries[1].label
                : fieldContextMenuEntries[0].label}
            </Box>
            <ChevronRight />
          </Button>
          <GenericMenu
            entries={fieldContextMenuEntries}
            onClose={handleClose}
            open={isOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            selectedValue={currentContext}
            onClick={(event, itemValue) => {
              currentSetContext(itemValue as FieldContext);
              handleClose();
              setSweepTimeout(() => {
                nestedSelectorRef.current?.reset();
              }, 0);
            }}
          />
        </Box>
      </>
    );
  };

  const _ActionButton =
    !hideCreateFieldDialog && !hideActionButton
      ? ({ closeSelector }: { closeSelector: () => void }) => (
          <AddFieldButton
            onAddFieldClick={() => {
              setIsCreateFieldDialogOpened(true);
              closeSelector();
            }}
          />
        )
      : undefined;

  return (
    <>
      <NestedSelector
        ref={nestedSelectorRef}
        objectType={objectType}
        placeholder={placeholder || 'Field'}
        displayLeadingObjectName={displayLeadingObjectName}
        readonly={readonly}
        startIcon={startIcon}
        loadingItem={loadingItem}
        customButtonText={customButtonText}
        customButtonSx={customButtonSx}
        useCustomButton={useCustomButton}
        customButtonStartIcon={customButtonStartIcon}
        customButtonEndIcon={customButtonEndIcon}
        CustomButtonComponent={CustomButtonComponent}
        onChange={(item, fullPath) => {
          if (onChange) {
            const _fullPath = removeLookupFromPathValue(fullPath);
            const fieldLabels = [objectType, ..._fullPath.map((path) => path.label || '')];
            const fieldIds = _fullPath.map((path) => path.value);
            const sweepField = item.data as SweepField;

            onChange({
              sweepField,
              fieldIds,
              fieldLabels,
              fullPath: _fullPath,
              fieldContext: displayFieldContextMenu ? localFieldContext : undefined,
              valueContext: displayValueContextMenu ? localValueContext : undefined,
              sfDisplayType: sweepField.sfDisplayType,
            });
          }
        }}
        onClose={() => {
          setLocalFieldContext(fieldContext);
          setLocalValueContext(valueContext);
        }}
        nestedPath={replaceEmptyLabelsByApiName(nestedPath)}
        resolveOpen={resolveOpen}
        resolveChildren={resolveChildren}
        additionalPopoverStyles={additionalPopoverStyles}
        ActionButton={_ActionButton}
        EmptyState={({ closeSelector }: { closeSelector: () => void }) => (
          <FieldSelectorEmptyState
            onAddFieldClick={
              !hideCreateFieldDialog && !hideActionButton
                ? () => {
                    setIsCreateFieldDialogOpened(true);
                    closeSelector();
                  }
                : undefined
            }
          />
        )}
        nestedSelectorButtonStyle={nestedSelectorButtonStyle}
        dropDownWidth={dropDownWidth}
        HeaderComponent={
          Boolean(displayFieldContextMenu || displayValueContextMenu)
            ? priorValueSelectorHeaderComponent()
            : undefined
        }
        labelPretext={
          fieldContext === FieldContext.PRIOR_VALUE || valueContext === FieldContext.PRIOR_VALUE
            ? 'Prior value of '
            : undefined
        }
      />
      {!hideCreateFieldDialog && (
        <CreateEditFieldDialog
          crmOrgId={crmOrgId}
          onCreateFieldCb={(field) => {
            const isChangedObjectType = objectType !== field.objectName;
            if (isChangedObjectType) {
              addNotification({
                message: `Field was created but isn't currently visible`,
                variant: SweepNotificationVariant.Success,
              });
            } else {
              onChangeWithNewField(field);
            }
          }}
          isOpened={isCreateFieldDialogOpened}
          closeDialog={() => setIsCreateFieldDialogOpened(false)}
          initialValues={{ objectName: objectType as SFDCObjectType }}
          mode={EditMode.CREATE}
        />
      )}
    </>
  );
};
